import { AlertCircle, Check, Loader2, Play, Zap } from "lucide-react";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";

import {
  ExtractionType,
  ExtractionResults,
  ExtractionState,
  ExtractDatesResponse,
  ExtractChecklistResponse,
  ExtractLocationResponse,
  ExtractBudgetResponse,
  ExtractClientResponse,
  ExtractNotesResponse,
  EXTRACTION_OPERATIONS,
} from "./types";

/** Returns a short label for the result count, e.g. "3 dates" or "Extracted". */
const getResultLabel = (type: ExtractionType, results: ExtractionResults): string => {
  switch (type) {
    case "dates": {
      const count = (results as ExtractDatesResponse).date_cards?.length ?? 0;
      return count === 1 ? "1 date" : `${count} dates`;
    }
    case "summary":
      return "Extracted";
    case "checklist": {
      const count = (results as ExtractChecklistResponse).checklists?.length ?? 0;
      return count === 1 ? "1 list" : `${count} lists`;
    }
    case "location": {
      const count = (results as ExtractLocationResponse).locations?.length ?? 0;
      return count === 1 ? "1 location" : `${count} locations`;
    }
    case "budget": {
      const count = (results as ExtractBudgetResponse).budgets?.length ?? 0;
      return count === 1 ? "1 budget" : `${count} budgets`;
    }
    case "client": {
      const count = (results as ExtractClientResponse).clients?.length ?? 0;
      return count === 1 ? "1 client" : `${count} clients`;
    }
    case "notes": {
      const count = (results as ExtractNotesResponse).notes?.length ?? 0;
      return count === 1 ? "1 note" : `${count} notes`;
    }
    default:
      return "Extracted";
  }
};

const getLoadingPhaseLabel = (loadingPhase: ExtractionState["loadingPhase"]): string => {
  if (loadingPhase === "analyzing") return "Analyzing...";
  if (loadingPhase === "extracting") return "Extracting...";
  return "Processing...";
};

interface ExtractionOperationListProps {
  onSelectOperation: (type: ExtractionType) => void;
  onRunAll: () => void;
  extractionStates: Partial<Record<ExtractionType, ExtractionState>>;
}

export const ExtractionOperationList = ({ onSelectOperation, onRunAll, extractionStates }: ExtractionOperationListProps) => {
  const types = EXTRACTION_OPERATIONS.map((op) => op.type);

  const anyLoading = types.some((t) => extractionStates[t]?.loading);
  const allDone = types.every((t) => !!extractionStates[t]?.results);
  const allBusy = types.every((t) => extractionStates[t]?.loading || extractionStates[t]?.results);
  const noneDone = types.every((t) => !extractionStates[t]);
  const someDone = !noneDone && !allDone;
  const doneCount = types.filter((t) => !!extractionStates[t]?.results).length;

  const allLoading = types.every((t) => extractionStates[t]?.loading);
  const showExtractAll = !allDone;
  const extractAllLabel = allLoading ? "Extracting..." : noneDone ? "Extract All" : allBusy ? "Extracting..." : someDone ? "Extract Remaining" : "Re-extract All";

  return (
    <div className="flex flex-col gap-2 p-4">
      {/* Extract All shortcut */}
      {showExtractAll && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2 mb-1 border-dashed"
          onClick={onRunAll}
          disabled={allBusy}
        >
          <Zap className="w-3.5 h-3.5" />
          {extractAllLabel}
          {(anyLoading || someDone) && (
            <span className="ml-auto text-xs text-muted-foreground font-mono">
              {doneCount}/{types.length}
            </span>
          )}
          {anyLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </Button>
      )}

      {EXTRACTION_OPERATIONS.map((operation) => {
        const Icon = operation.icon;
        const state = extractionStates[operation.type];
        const isLoading = state?.loading ?? false;
        const isDone = !isLoading && !!state?.results;
        const hasError = !isLoading && !!state?.error;

        return (
          <button
            key={operation.type}
            type="button"
            className={cn(
              "flex items-center gap-3 w-full p-3 rounded-swivu-base border text-left group",
              "transition-all duration-200",
              // Idle
              !state && "border-border hover:bg-accent hover:border-primary/30 active:bg-accent/80",
              // Loading
              isLoading && "border-primary/30 bg-primary/[0.04] cursor-default",
              // Done
              isDone && "border-primary/20 bg-primary/[0.03] hover:bg-accent hover:border-primary/30 active:bg-accent/80",
              // Error
              hasError && "border-destructive/20 bg-destructive/[0.03] hover:bg-destructive/[0.06] hover:border-destructive/30 active:bg-destructive/10",
            )}
            onClick={() => onSelectOperation(operation.type)}
          >
            {/* Icon area */}
            <div className={cn(
              "flex items-center justify-center w-9 h-9 rounded-md shrink-0 transition-colors",
              !state && "bg-primary/10 text-primary group-hover:bg-primary/15",
              isLoading && "bg-primary/10 text-primary",
              isDone && "bg-green-500/10 text-green-600 dark:text-green-400",
              hasError && "bg-destructive/10 text-destructive",
            )}>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isDone && <Check className="w-4 h-4" />}
              {hasError && <AlertCircle className="w-4 h-4" />}
              {!state && Icon && <Icon className="w-4 h-4" />}
            </div>

            {/* Label / description */}
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground">{operation.label}</span>
              <span className={cn(
                "text-xs truncate",
                isLoading && "text-primary/70",
                isDone && "text-muted-foreground",
                hasError && "text-destructive/80",
                !state && "text-muted-foreground",
              )}>
                {isLoading && getLoadingPhaseLabel(state?.loadingPhase ?? null)}
                {isDone && getResultLabel(operation.type, state!.results!)}
                {hasError && (state!.error!.length > 48 ? state!.error!.slice(0, 48) + "..." : state!.error)}
                {!state && operation.description}
              </span>
            </div>

            {/* Right-side affordance */}
            <span className={cn(
              "text-xs shrink-0",
              isLoading && "text-primary/50",
              isDone && "text-muted-foreground",
              hasError && "text-destructive/70",
            )}>
              {isLoading && <Play className="w-3 h-3 opacity-50" />}
              {isDone && "View →"}
              {hasError && "Retry →"}
            </span>
          </button>
        );
      })}
    </div>
  );
};
